import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  SimulatedXeroDocument,
  SimulatedXeroDocumentType,
  SimulatedXeroFailNextInput,
} from "@shared-core/data/journey-testing-types";
import { formatPreviewTime } from "@shared-core/domain/preview-clock";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import {
  EmptyRow,
  FailedRow,
  LoadingRow,
  describeError,
  useLoaded,
} from "./consoleState";

const DOCUMENT_TYPES: Array<{
  value: SimulatedXeroDocumentType | "all";
  label: string;
}> = [
  { value: "all", label: "All documents" },
  { value: "ACCREC", label: "Sales invoices" },
  { value: "ACCRECCREDIT", label: "Sales credit notes" },
  { value: "ACCPAY", label: "Purchase invoices" },
  { value: "PURCHASEORDER", label: "Purchase orders" },
  { value: "CONTACT", label: "Contacts" },
];

const OPERATIONS: SimulatedXeroFailNextInput["operation"][] = [
  "post_invoice",
  "post_credit_note",
  "post_invoices_batch",
  "post_supplier_credit_notes_batch",
  "post_payments_batch",
  "put_credit_note_allocation",
  "post_purchase_order",
  "create_contact",
  "get_invoice",
  "list_invoices_modified_since",
  "list_credit_notes_modified_since",
];
const FAILURES: SimulatedXeroFailNextInput["failure"][] = [
  "validation",
  "throttled",
  "server",
];

/** Xero (07 §1): the simulated Xero's documents, and finance's side of them — paid, voided, edited, or failing. */
export function XeroTab() {
  const adapter = useDataAdapter();
  const [page, setPage] = useState(1);
  const [documentType, setDocumentType] = useState<
    SimulatedXeroDocumentType | "all"
  >("all");
  const loadDocuments = useCallback(
    () =>
      adapter.listSimulatedXeroDocuments({
        page,
        pageSize: 25,
        documentType: documentType === "all" ? undefined : documentType,
      }),
    [adapter, page, documentType],
  );
  const [documents, reload] = useLoaded(loadDocuments);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="xero-type">Type</Label>
          <Select
            value={documentType}
            onValueChange={(value) => {
              setDocumentType(value as SimulatedXeroDocumentType | "all");
              setPage(1);
            }}
          >
            <SelectTrigger id="xero-type" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <FailNextForm />
      </div>
      {documents.status === "loading" && (
        <LoadingRow label="Loading the simulated Xero…" />
      )}
      {documents.status === "failed" && (
        <FailedRow message={documents.message} onRetry={reload} />
      )}
      {documents.status === "loaded" && documents.data.total === 0 && (
        <EmptyRow>
          Nothing has been pushed to Xero yet — invoices appear once the ledger
          pushes them.
        </EmptyRow>
      )}
      {documents.status === "loaded" && documents.data.total > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Number</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Changed</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.data.items.map((doc) => (
                <XeroDocumentRow key={doc.id} doc={doc} onChanged={reload} />
              ))}
            </TableBody>
          </Table>
          <ListPagination
            page={documents.data.page}
            totalPages={documents.data.totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function XeroDocumentRow({
  doc,
  onChanged,
}: {
  doc: SimulatedXeroDocument;
  onChanged: () => void;
}) {
  const adapter = useDataAdapter();
  const [action, setAction] = useState<"pay" | "edit" | null>(null);
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState("");
  const [total, setTotal] = useState(String(doc.total));
  const [busy, setBusy] = useState(false);

  async function run(label: string, write: () => Promise<unknown>) {
    setBusy(true);
    try {
      await write();
      toast.success(label);
      setAction(null);
      onChanged();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  const payable = doc.status === "AUTHORISED" && Number(doc.amount_due) > 0;
  return (
    <>
      <TableRow>
        <TableCell>{doc.document_type}</TableCell>
        <TableCell className="font-mono">{doc.number ?? "—"}</TableCell>
        <TableCell>{doc.reference ?? "—"}</TableCell>
        <TableCell className="font-mono text-xs">
          {doc.contact_id ?? "—"}
        </TableCell>
        <TableCell className="text-right">{String(doc.total)}</TableCell>
        <TableCell className="text-right">{String(doc.amount_due)}</TableCell>
        <TableCell>
          <Badge variant="outline">{doc.status}</Badge>
        </TableCell>
        <TableCell>{formatPreviewTime(doc.modified_at)}</TableCell>
        <TableCell className="space-x-1 whitespace-nowrap text-right">
          {payable && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setAction("pay")}
            >
              Mark paid
            </Button>
          )}
          {doc.status === "DRAFT" && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setAction("edit")}
            >
              Edit draft
            </Button>
          )}
          {doc.status !== "VOIDED" && doc.status !== "PAID" && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() =>
                void run(
                  `Voided ${doc.number ?? doc.reference ?? doc.id}.`,
                  () => adapter.voidSimulatedXeroDocument(doc.id),
                )
              }
            >
              Void
            </Button>
          )}
        </TableCell>
      </TableRow>
      {action && (
        <TableRow>
          <TableCell colSpan={9}>
            <div className="flex flex-wrap items-end gap-3">
              {action === "pay" ? (
                <>
                  <Inline
                    id={`${doc.id}-amount`}
                    label={`Amount (all ${String(doc.amount_due)} when empty)`}
                    value={amount}
                    onChange={setAmount}
                  />
                  <Inline
                    id={`${doc.id}-paid-on`}
                    label="Paid on (today when empty)"
                    value={paidOn}
                    onChange={setPaidOn}
                    type="date"
                  />
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        `Marked paid in Xero; the next inbound sync picks it up.`,
                        () =>
                          adapter.markSimulatedXeroPaid(doc.id, {
                            amount: amount || undefined,
                            paidOn: paidOn || undefined,
                          }),
                      )
                    }
                  >
                    Mark paid
                  </Button>
                </>
              ) : (
                <>
                  <Inline
                    id={`${doc.id}-total`}
                    label="Total"
                    value={total}
                    onChange={setTotal}
                  />
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void run("Draft edited.", () =>
                        adapter.editSimulatedXeroDraft(doc.id, total),
                      )
                    }
                  >
                    Save
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => setAction(null)}>
                Cancel
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function FailNextForm() {
  const adapter = useDataAdapter();
  const [operation, setOperation] =
    useState<SimulatedXeroFailNextInput["operation"]>("post_invoice");
  const [failure, setFailure] =
    useState<SimulatedXeroFailNextInput["failure"]>("server");
  const [busy, setBusy] = useState(false);

  async function arm() {
    setBusy(true);
    try {
      await adapter.failNextSimulatedXeroCall({ operation, failure });
      toast.success(`The next ${operation} will fail (${failure}).`);
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
      <div className="grid gap-1">
        <Label htmlFor="xero-fail-operation" className="text-xs">
          Fail next
        </Label>
        <Select
          value={operation}
          onValueChange={(v) =>
            setOperation(v as SimulatedXeroFailNextInput["operation"])
          }
        >
          <SelectTrigger id="xero-fail-operation" className="h-8 w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPERATIONS.map((op) => (
              <SelectItem key={op} value={op}>
                {op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1">
        <Label htmlFor="xero-fail-failure" className="text-xs">
          With
        </Label>
        <Select
          value={failure}
          onValueChange={(v) =>
            setFailure(v as SimulatedXeroFailNextInput["failure"])
          }
        >
          <SelectTrigger id="xero-fail-failure" className="h-8 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FAILURES.map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => void arm()}
      >
        Fail next push
      </Button>
    </div>
  );
}

function Inline({
  id,
  label,
  value,
  onChange,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div className="grid gap-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        className="h-8 w-48"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
